"use client";

import * as React from "react";
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { ChartTooltip, cn, BarChartProps } from "visuais";

export function BarChart({
  data,
  config,
  className,
  height = 250,
  width = "100%",
  xKey,
  yKeys,
  variant = "default",
  barSize = 20,
  barGap = 4,
  stacked = false,
  horizontal = false,
  showTooltip = true,
  showGrid = true,
  showLegend = false,
  showXAxis = true,
  showYAxis = true,
  showLabels = false,
  customLabels = false,
  mixed = false,
  positiveColor = "hsl(var(--chart-1))",
  negativeColor = "hsl(var(--chart-2))",
  loading = false,
  error = null,
  xAxisFormatter,
  yAxisFormatter,
}: BarChartProps) {
  const stackId = stacked ? "stack" : undefined;

  // Determinar se é gráfico tradicional (uma série)
  const isTraditional = variant === "traditional" || yKeys.length === 1;

  // Determinar se é múltiplo (múltiplas séries)
  const isMultiple = variant === "multiple" || yKeys.length > 1;

  // Determinar se é mixado (positivo/negativo)
  const isMixed = variant === "mixed" || mixed;

  // Verificar se há dados válidos
  const hasValidData = data && Array.isArray(data) && data.length > 0;

  // Verificar se há pelo menos um valor não-zero nos dados
  const hasNonZeroValues =
    hasValidData &&
    data.some((item: any) => {
      return yKeys.some((key) => {
        const value = item[key];
        return value !== undefined && value !== null && value > 0;
      });
    });

  if (loading) {
    return (
      <div
        className={cn("flex items-center justify-center", className)}
        style={{ height, width }}
      >
        <div className="text-muted-foreground text-sm">Carregando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={cn("flex items-center justify-center", className)}
        style={{ height, width }}
      >
        <div className="text-destructive text-sm text-center">
          Erro: {error}
        </div>
      </div>
    );
  }

  if (!hasValidData || !hasNonZeroValues) {
    return (
      <div
        className={cn("flex items-center justify-center", className)}
        style={{ height, width }}
      >
        <div className="text-muted-foreground text-sm">
          Sem dados disponíveis
        </div>
      </div>
    );
  }

  return (
    <div className={cn("w-full", className)} style={{ height, width }}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsBarChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          barSize={barSize}
          barGap={barGap}
          layout={horizontal ? "horizontal" : "vertical"}
        >
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={!horizontal}
              horizontal={horizontal}
            />
          )}

          {showXAxis && (
            <XAxis
              dataKey={horizontal ? yKeys[0] : xKey}
              type={horizontal ? "number" : "category"}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={horizontal ? yAxisFormatter : xAxisFormatter}
            />
          )}

          {showYAxis && (
            <YAxis
              dataKey={horizontal ? xKey : yKeys[0]}
              type={horizontal ? "category" : "number"}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              domain={horizontal ? undefined : [0, "auto"]}
              tickFormatter={horizontal ? xAxisFormatter : yAxisFormatter}
            />
          )}

          {showTooltip && (
            <ChartTooltip
              cursor={false}
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;

                const data = payload[0];
                const dataKey = data.dataKey as string;
                const seriesConfig = config[dataKey];
                const color = seriesConfig?.color || "#3b82f6";

                return (
                  <div className="rounded-lg border bg-background p-2 shadow-sm">
                    <div className="grid gap-2">
                      <div className="flex items-center gap-2">
                        <div
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: color }}
                        />
                        <span className="font-medium">{label}</span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {data.value?.toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              }}
            />
          )}

          {yKeys.map((key, index) => {
            const seriesConfig = config[key];
            if (!seriesConfig) return null;

            // Definir cor padrão baseada no índice
            const defaultColors = [
              "hsl(199, 89%, 48%)", // cyan-500
              "hsl(221, 83%, 53%)", // indigo-500
              "hsl(217, 91%, 60%)", // blue-500
              "hsl(142, 76%, 36%)", // green-600
              "hsl(25, 95%, 53%)", // orange-500
            ];

            let color =
              seriesConfig.color || defaultColors[index % defaultColors.length];

            // Para gráficos mixados, usar cores baseadas no valor
            if (isMixed) {
              color = positiveColor;
            }

            // Garantir que a cor seja uma string válida
            if (
              !color ||
              color === "undefined" ||
              color === "null" ||
              color.includes("var(--chart-")
            ) {
              color = defaultColors[index % defaultColors.length];
            }

            return (
              <Bar key={key} dataKey={key} stackId={stackId} fill={color}>
                {showLabels && (
                  <LabelList
                    dataKey={key}
                    position={horizontal ? "right" : "top"}
                    formatter={(value: any) => {
                      if (customLabels) {
                        // Aqui você pode implementar lógica customizada para labels
                        return value;
                      }
                      return value;
                    }}
                  />
                )}
              </Bar>
            );
          })}
        </RechartsBarChart>
      </ResponsiveContainer>

      {showLegend && (
        <div className="mt-4">
          <div className="flex flex-wrap gap-4 justify-center">
            {yKeys.map((key, index) => {
              const seriesConfig = config[key];
              if (!seriesConfig) return null;

              const defaultColors = [
                "hsl(199, 89%, 48%)", // cyan-500
                "hsl(221, 83%, 53%)", // indigo-500
                "hsl(217, 91%, 60%)", // blue-500
                "hsl(142, 76%, 36%)", // green-600
                "hsl(25, 95%, 53%)", // orange-500
              ];

              const color =
                seriesConfig.color ||
                defaultColors[index % defaultColors.length];

              return (
                <div key={key} className="flex items-center gap-2">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-sm text-muted-foreground">
                    {seriesConfig.label || key}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
